import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { LuxuryButton } from "./LuxuryButton";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { id: "philosophy", label: "Philosophy", href: "#philosophy" },
  { id: "journey", label: "The Journey", href: "#journey" },
  { id: "programmes", label: "Programmes", href: "#programmes" },
  { id: "practitioners", label: "Practitioners", href: "#practitioners" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const onDark = !scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled || open
          ? "border-b border-border/60 bg-background/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 lg:px-12">
        <button data-testid="nav-logo" onClick={() => go("#top")} aria-label="AYURENEW home">
          <Logo light={onDark} />
        </button>
        <nav className="hidden items-center gap-10 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.href)}
              className={`text-[11px] font-medium uppercase tracking-[0.3em] transition-colors duration-300 ${
                onDark
                  ? "text-[#F9F6F0]/75 hover:text-[#E9B277]"
                  : "text-foreground/70 hover:text-accent"
              }`}
            >
              {l.label}
            </button>
          ))}
          <LuxuryButton data-testid="nav-cta" className="px-6 py-3" onClick={() => go("#contact")}>
            Book a Discovery Call
          </LuxuryButton>
        </nav>
        <button
          data-testid="nav-menu-toggle"
          className={`lg:hidden ${onDark ? "text-[#F9F6F0]" : "text-foreground"}`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col gap-6 border-t border-border/60 px-6 py-8 lg:hidden"
          >
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => go(l.href)}
                className="text-left text-xs font-medium uppercase tracking-[0.3em] text-foreground/80"
              >
                {l.label}
              </button>
            ))}
            <LuxuryButton data-testid="mobile-nav-cta" onClick={() => go("#contact")}>
              Book a Discovery Call
            </LuxuryButton>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
